import { useState } from 'react';
import PropTypes from 'prop-types';
import * as analytics from 'Utils/analytics';
import DeleteLibraryItem from 'Components/profile/DeleteLibraryItem';
import IconSvg from 'Components/IconSvg';
const { EVENTS } = analytics;
const ICONS = {
  EVENT_PROFILE: 'event',
  EVENT_SERIES: 'event',
  FILM: 'film',
  INFO: 'info',
  SHOW: 'show',
  STORY: 'story',
  VIDEO: 'video',
  VIDEO_360: 'video360',
};

const formatVideoDuration = (strings, duration) => {
  const hours = ~~(duration / 3600);
  let minutes = ~~(duration / 60) - hours * 60;
  let msg = `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
  if (!hours) msg = `${minutes}`;
  if (!hours && !minutes) msg = '1';
  return `${msg} ${strings.min}`;
};

const formatShowCount = (seasonCount, episodeCount, strings) => {
  const seasonMsg = `${seasonCount} ${
    seasonCount > 1 ? strings.seasons : strings.season
  }`;
  const episodeMsg = `${episodeCount} ${
    episodeCount > 1 ? strings.episodes : strings.episode
  }`;
  return episodeCount ? `${seasonMsg} • ${episodeMsg}` : seasonMsg;
};

const formatMetadata = ({
  episodeCount,
  isGeoBlocked,
  seasonCount,
  strings,
  videoDurationSeconds,
  author,
}) => {
  let icon;
  let metadata = '';
  if (!!videoDurationSeconds)
    metadata = formatVideoDuration(strings, videoDurationSeconds);
  if (!!seasonCount)
    metadata = formatShowCount(seasonCount, episodeCount, strings);
  if (!!author) metadata = author;
  if (isGeoBlocked) {
    icon = ICONS.INFO;
    metadata = strings.thisVideo;
  }
  return (
    <p className="library-item__meta">
      {icon && <IconSvg title={metadata} type={icon} />}
      <span>{metadata}</span>
    </p>
  );
};

const LibraryItem = ({
  country,
  description,
  episodeCount,
  geoBlockedIn,
  id,
  imageSrc,
  isVideo,
  onDelete,
  tag,
  teaser,
  title,
  type,
  seasonCount,
  strings,
  style,
  url,
  videoDurationSeconds,
  author,
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const isGeoBlocked = geoBlockedIn.includes(country);
  const handleClick = () => {
    analytics.event({ action: EVENTS.selectLibraryItem, label: id });
  };

  return (
    <a
      href={url}
      style={style}
      className={`library-item${isVideo ? ' --video' : ''}${
        isGeoBlocked ? ' --geo-blocked' : ''
      }${isDisabled ? ' --disabled' : ''}`}
      onClick={handleClick}
    >
      <div className="library-item__media">
        <img alt={title} title={title} src={imageSrc} />
        <div className="library-item__play-button">
          <IconSvg type="play" />
        </div>
        {isVideo &&
          formatMetadata({
            isGeoBlocked,
            strings,
            videoDurationSeconds,
          })}
      </div>
      <div className="library-item__content">
        {tag && <label className="library-item__tag">{tag}</label>}
        <p className="library-item__title">{title}</p>
        <p className="library-item__description">{description || teaser}</p>
        {formatMetadata({
          episodeCount,
          isGeoBlocked,
          seasonCount,
          strings,
          type,
          videoDurationSeconds,
          author,
        })}
      </div>
      <div className="library-item__more">
        <DeleteLibraryItem
          onClose={() => setIsDisabled(false)}
          onDelete={() => onDelete(id)}
          onOpen={() => setIsDisabled(true)}
          remove={strings.remove}
        />
      </div>
      {isVideo && (
        <div className="library-item__sub-content">
          <p className="library-item__description">{description || teaser}</p>
        </div>
      )}
    </a>
  );
};

LibraryItem.propTypes = {
  country: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  episodeCount: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  geoBlockedIn: PropTypes.array,
  id: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  isVideo: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  tag: PropTypes.string.isRequired,
  teaser: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  seasonCount: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  strings: PropTypes.object.isRequired,
  style: PropTypes.object,
  url: PropTypes.string.isRequired,
  videoDurationSeconds: PropTypes.number,
  author: PropTypes.string,
};

LibraryItem.defaultProps = {
  style: {},
};

export default LibraryItem;
